import React, { useId } from 'react';

import type { ChoiceCardSelectProps } from './ChoiceCardSelect.types';

/**
 * Single-select group of "choice cards with description".
 *
 * Each card is a real (sr-only) radio inside a styled `<label>`, so native radio
 * semantics — single selection, arrow-key navigation, form a11y — come for free.
 * Selection uses the design-system treatment: sakura wash + accent-strong border.
 *
 * An option may be `disabled` when the choice exists but is not currently
 * obtainable. Pair that with a `disabledReason` so the card explains itself
 * instead of looking broken.
 */
export function ChoiceCardSelect({
  options,
  value,
  onChange,
  ariaLabel,
  className = '',
}: ChoiceCardSelectProps): React.JSX.Element {
  const name = useId();

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`flex flex-col gap-3 ${className}`.trim()}
    >
      {options.map((option) => {
        const disabled = option.disabled ?? false;
        const selected = !disabled && value === option.value;
        return (
          <label
            key={option.value}
            className={`block rounded-lg border-[1.5px] px-4 py-3 transition-colors duration-fast ease-out focus-within:ring-state-focus ${
              disabled
                ? 'cursor-not-allowed border-stroke-subtle bg-lift-1 opacity-40'
                : selected
                  ? 'cursor-pointer border-stroke-accent bg-sakura-wash'
                  : 'cursor-pointer border-stroke-default bg-lift-1 hover:border-stroke-strong'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={selected}
              disabled={disabled}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            <span className="block text-body-md font-semibold text-neutral-50">
              {option.title}
            </span>
            <span className="mt-1 block text-body-sm text-neutral-400">
              {option.description}
            </span>
            {disabled && option.disabledReason && (
              <span className="mt-2 block text-label-sm text-warning-400">
                {option.disabledReason}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}

export default ChoiceCardSelect;
